import React, { useRef, useState } from "react";
import {
  ChevronDown,
  ChevronUp,
  GripVertical,
  Heading,
  ImageIcon,
  List,
  Plus,
  Quote,
  Trash2,
  Type,
} from "lucide-react";
import { Button, DropdownMenu, Input, Textarea } from "@/components/ui";
import { Blog } from "@/services/bedrock";
import { cn } from "@/lib/utils";
import { BLOG_BLOCK_TYPES, emptyBlogBlock, moveItem, normalizeBlogSection } from "../blogBody";
import { BlogMediaBlock } from "./BlogMediaBlock";
import { beginPointerReorder, ReorderGhost, ReorderGhostOverlay } from "./pointerReorder";

type Props = {
  section: Blog.Section;
  sectionIndex: number;
  readOnly: boolean;
  defaultOpen?: boolean;
  dragging?: boolean;
  onChange: (section: Blog.Section) => void;
  onRemove: () => void;
  onHover?: (index: number | null) => void;
  onExpand?: (index: number, blockIndex?: number) => void;
  onInputBlur?: () => void;
  onMoveStart?: (event: React.PointerEvent, index: number, card: HTMLElement) => void;
};

const BLOCK_STYLE: Record<
  Exclude<Blog.BlockType, "heading">,
  { label: string; icon: React.ElementType; chip: string; accent: string }
> = {
  body: {
    label: "Body",
    icon: Type,
    chip: "bg-sky-50 text-sky-700 border-sky-200",
    accent: "border-l-sky-400",
  },
  list: {
    label: "List",
    icon: List,
    chip: "bg-emerald-50 text-emerald-700 border-emerald-200",
    accent: "border-l-emerald-400",
  },
  pullQuote: {
    label: "Quote",
    icon: Quote,
    chip: "bg-amber-50 text-amber-800 border-amber-200",
    accent: "border-l-amber-400",
  },
  media: {
    label: "Media",
    icon: ImageIcon,
    chip: "bg-violet-50 text-violet-700 border-violet-200",
    accent: "border-l-violet-400",
  },
};

export const BlogSectionBlocks: React.FC<Props> = ({
  section,
  sectionIndex,
  readOnly,
  defaultOpen = false,
  dragging,
  onChange,
  onRemove,
  onHover,
  onExpand,
  onInputBlur,
  onMoveStart,
}) => {
  const [open, setOpen] = useState(defaultOpen);
  const cardRef = useRef<HTMLDivElement>(null);
  const [blockDragIndex, setBlockDragIndex] = useState<number | null>(null);
  const [blockGhost, setBlockGhost] = useState<ReorderGhost | null>(null);
  const contentRef = useRef<Blog.Block[]>([]);
  const listRef = useRef<HTMLDivElement>(null);
  const blocks = normalizeBlogSection(section).blocks ?? [];
  const heading = blocks[0]?.type === "heading" ? blocks[0] : emptyBlogBlock("heading");
  const contentBlocks = blocks.slice(1);
  const headingText = heading.text ?? "";
  contentRef.current = contentBlocks;

  const commit = (nextContent: Blog.Block[], nextHeading = heading) => {
    onChange({ blocks: [nextHeading, ...nextContent] });
  };

  const updateHeading = (text: string) => {
    commit(contentBlocks, { type: "heading", text });
  };

  const updateBlock = (index: number, patch: Partial<Blog.Block>) => {
    commit(contentBlocks.map((block, i) => (i === index ? { ...block, ...patch } : block)));
  };

  const addBlock = (type: Exclude<Blog.BlockType, "heading">) => {
    commit([...contentBlocks, emptyBlogBlock(type)]);
  };

  const removeBlock = (index: number) => {
    commit(contentBlocks.filter((_, i) => i !== index));
  };

  const moveBlock = (index: number, direction: -1 | 1) => {
    const nextIndex = index + direction;
    if (nextIndex < 0 || nextIndex >= contentBlocks.length) return;
    const next = [...contentBlocks];
    const [moved] = next.splice(index, 1);
    next.splice(nextIndex, 0, moved);
    commit(next);
  };

  const focusPreview = (blockIndex: number) => {
    onExpand?.(sectionIndex, blockIndex);
  };

  const blurPreview = (event: React.FocusEvent<HTMLElement>) => {
    if (event.currentTarget.contains(event.relatedTarget as Node)) return;
    if (!dragging) onInputBlur?.();
  };

  const startBlockDrag = (event: React.PointerEvent, index: number, card: HTMLElement) => {
    beginPointerReorder({
      event,
      card,
      index,
      itemSelector: "[data-blog-block]",
      scope: listRef.current,
      onReorder: (from, to) => {
        const next = moveItem(contentRef.current, from, to);
        contentRef.current = next;
        commit(next);
      },
      onActiveChange: setBlockDragIndex,
      onGhostChange: setBlockGhost,
    });
  };

  return (
    <div
      ref={cardRef}
      data-blog-section
      className={cn(
        "flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm transition-all duration-150",
        dragging && "opacity-40 border-indigo-200"
      )}
      onMouseLeave={() => {
        if (!dragging) onHover?.(null);
      }}
    >
      <div
        className="-mx-1 flex items-center justify-between gap-2 rounded-xl px-1 py-1 hover:bg-indigo-50/80"
        onMouseEnter={() => onHover?.(sectionIndex)}
        onMouseLeave={() => {
          if (!dragging) onHover?.(null);
        }}
      >
        <div
          role="button"
          tabIndex={readOnly ? -1 : 0}
          aria-label={`Drag section ${sectionIndex + 1}`}
          className={cn(
            "shrink-0 rounded-md p-1 text-slate-400 touch-none",
            readOnly
              ? "cursor-default opacity-40"
              : "cursor-grab active:cursor-grabbing hover:bg-white hover:text-slate-600"
          )}
          onPointerDown={event => {
            if (readOnly || !cardRef.current) return;
            onMoveStart?.(event, sectionIndex, cardRef.current);
          }}
        >
          <GripVertical className="size-4" />
        </div>
        <button
          type="button"
          className="flex min-w-0 flex-1 items-center gap-2 text-left"
          aria-expanded={open}
          aria-label={`${open ? "Collapse" : "Expand"} section ${sectionIndex + 1}`}
          onClick={() =>
            setOpen(current => {
              if (!current) onExpand?.(sectionIndex);
              return !current;
            })
          }
        >
          <ChevronDown className={cn("size-4 shrink-0 text-slate-500 transition-transform", !open && "-rotate-90")} />
          <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-indigo-600 text-xs font-bold text-white">
            {sectionIndex + 1}
          </span>
          <span className="min-w-0 truncate text-sm font-semibold text-slate-800">{headingText || "New section"}</span>
          {!open && (
            <span className="shrink-0 text-xs font-medium text-slate-400">
              {contentBlocks.length} {contentBlocks.length === 1 ? "component" : "components"}
            </span>
          )}
        </button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label={`Remove section ${sectionIndex + 1}`}
          disabled={readOnly}
          onClick={onRemove}
        >
          <Trash2 className="size-4" />
        </Button>
      </div>

      {open && (
        <div
          className="flex flex-col gap-2 rounded-xl border border-indigo-100 bg-indigo-50/40 p-3"
          onFocus={() => focusPreview(0)}
          onBlur={blurPreview}
        >
          <span className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide text-indigo-700">
            <Heading className="size-3" />
            Heading
            <span className="font-medium normal-case tracking-normal text-indigo-500">optional</span>
          </span>
          <Input
            id={`blog-section-${sectionIndex}-heading`}
            value={headingText}
            disabled={readOnly}
            placeholder="Section heading"
            className="bg-white text-base font-semibold"
            onChange={event => updateHeading(event.target.value)}
          />
        </div>
      )}

      {open && (
        <div ref={listRef} className="flex flex-col gap-3">
          {contentBlocks.map((block, index) => {
            const inputId = `blog-section-${sectionIndex}-block-${index}`;
            const style = BLOCK_STYLE[block.type as Exclude<Blog.BlockType, "heading">];
            if (!style) return null;
            const Icon = style.icon;
            return (
              <div
                key={`${block.type}-${index}`}
                data-blog-block
                className={cn(
                  "flex flex-col gap-2 rounded-xl border border-slate-200 bg-slate-50/60 p-3 border-l-4",
                  style.accent,
                  blockDragIndex === index && "opacity-40"
                )}
                onFocus={() => focusPreview(index + 1)}
                onBlur={blurPreview}
              >
                <div className="flex items-center gap-1">
                  <span
                    className={cn(
                      "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide",
                      style.chip
                    )}
                  >
                    <Icon className="size-3" />
                    {style.label}
                  </span>
                  <div className="flex-grow" />
                  <div
                    role="button"
                    tabIndex={readOnly ? -1 : 0}
                    aria-label={`Drag ${style.label}`}
                    className={cn(
                      "rounded-md p-1 text-slate-400 touch-none",
                      readOnly
                        ? "cursor-default opacity-40"
                        : "cursor-grab active:cursor-grabbing hover:bg-white hover:text-slate-600"
                    )}
                    onPointerDown={event => {
                      if (readOnly) return;
                      startBlockDrag(event, index, event.currentTarget.closest("[data-blog-block]") as HTMLElement);
                    }}
                  >
                    <GripVertical className="size-4" />
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="size-7"
                    aria-label="Move up"
                    disabled={readOnly || index === 0}
                    onClick={() => moveBlock(index, -1)}
                  >
                    <ChevronUp className="size-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="size-7"
                    aria-label="Move down"
                    disabled={readOnly || index === contentBlocks.length - 1}
                    onClick={() => moveBlock(index, 1)}
                  >
                    <ChevronDown className="size-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="size-7"
                    aria-label={`Remove ${style.label}`}
                    disabled={readOnly}
                    onClick={() => removeBlock(index)}
                  >
                    <Trash2 className="size-4" />
                  </Button>
                </div>

                {block.type === "body" && (
                  <Textarea
                    id={inputId}
                    rows={5}
                    value={(block.paragraphs ?? []).join("\n\n")}
                    disabled={readOnly}
                    placeholder="Write the paragraph…"
                    className="bg-white"
                    onChange={event =>
                      updateBlock(index, {
                        paragraphs: event.target.value.split(/\n\s*\n/).map(paragraph => paragraph.trim()),
                      })
                    }
                  />
                )}

                {block.type === "list" && (
                  <Textarea
                    id={inputId}
                    rows={3}
                    value={(block.items ?? []).join("\n")}
                    disabled={readOnly}
                    placeholder="One item per line"
                    className="bg-white"
                    onChange={event =>
                      updateBlock(index, {
                        items: event.target.value.split("\n").map(item => item.trim()),
                      })
                    }
                  />
                )}

                {block.type === "pullQuote" && (
                  <Input
                    id={inputId}
                    value={block.text ?? ""}
                    disabled={readOnly}
                    placeholder="A memorable line"
                    className="bg-white italic"
                    onChange={event => updateBlock(index, { text: event.target.value })}
                  />
                )}

                {block.type === "media" && (
                  <BlogMediaBlock
                    block={block}
                    readOnly={readOnly}
                    inputId={inputId}
                    onChange={patch => updateBlock(index, patch)}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}

      {open && (
        <DropdownMenu>
          <DropdownMenu.Trigger
            render={
              <Button type="button" variant="outline" size="sm" disabled={readOnly} className="self-start">
                <Plus className="size-4" />
                Add component
              </Button>
            }
          />
          <DropdownMenu.Content>
            {BLOG_BLOCK_TYPES.map(option => (
              <DropdownMenu.Item key={option.type} onClick={() => addBlock(option.type)}>
                {option.label}
              </DropdownMenu.Item>
            ))}
          </DropdownMenu.Content>
        </DropdownMenu>
      )}
      <ReorderGhostOverlay ghost={blockGhost} />
    </div>
  );
};
